import { useState, useEffect } from 'react'

import { useAppSelector } from 'app-redux/hooks'
import { selectCardMap, selectCategories } from 'app-redux/deck/selectors'

import { CardType, COLLECTION_STATUS_NAME } from 'types/deck'

const extractCardData = (cards: CardType[]) => {
  const colorFilterOptions: Record<string, boolean> = {}
  const keywordFilterOptions: Record<string, boolean> = {}
  const typeFilterOptions: Record<string, boolean> = {}
  const subTypeFilterOptions: Record<string, boolean> = {}
  const superTypeFilterOptions: Record<string, boolean> = {}
  const colorTagFilterOptions: Record<string, boolean> = {}
  const categoryFilterOptions: Record<string, boolean> = {}
  const collectionFilterOptions: Record<string, boolean> = {}
  const layoutFilterOptions: Record<string, boolean> = {}

  for (const card of cards) {
    const colors = card.front && card.back ? [...card.front.colors, ...card.back.colors] : card.colors
    const types = card.front && card.back ? [...card.front.types, ...card.back.types] : card.types
    const superTypes = card.front && card.back ? [...card.front.superTypes, ...card.back.superTypes] : card.superTypes
    const subTypes = card.front && card.back ? [...card.front.subTypes, ...card.back.subTypes] : card.subTypes

    card.keywords.forEach(keyword => (keywordFilterOptions[keyword] = true))
    card.categories.forEach(category => (categoryFilterOptions[category] = true))
    collectionFilterOptions[COLLECTION_STATUS_NAME[card.owned]] = true
    colorTagFilterOptions[card.colorLabel.name] = true
    colors.forEach(color => (colorFilterOptions[color] = true))
    types.forEach(type => (typeFilterOptions[type] = true))
    superTypes.forEach(superType => (superTypeFilterOptions[superType] = true))
    subTypes.forEach(subType => (subTypeFilterOptions[subType] = true))
    layoutFilterOptions[card.layout] = true
  }

  const formatData = (data: Record<string, boolean>) => Object.keys(data).sort()

  return {
    colors: formatData(colorFilterOptions),
    keywords: formatData(keywordFilterOptions),
    types: formatData(typeFilterOptions),
    subTypes: formatData(subTypeFilterOptions),
    superTypes: formatData(superTypeFilterOptions),
    colorTags: formatData(colorTagFilterOptions),
    categories: formatData(categoryFilterOptions),
    collectionStatus: formatData(collectionFilterOptions),
    layouts: formatData(layoutFilterOptions),
  }
}

const useGetAdvancedFilterOptions = (cardsToFilter: CardType[]) => {
  // colors - filter to color id of deck
  // keywords - unique list of keywords on cards in the deck
  // type - unique list of supertypes, subtypes, and types in the deck
  // mana value - unique list of mana values in the deck
  // color tags - unique list of color tags
  // categories - list of all deck categories
  // collection - owned, not owned, or printing

  const [colorFilterOptions, setColorFilterOptions] = useState<string[]>([])
  const [keywordFilterOptions, setKeywordFilterOptions] = useState<string[]>([])
  const [typeFilterOptions, setTypeFilterOptions] = useState<string[]>([])
  const [superTypeFilterOptions, setSuperTypeFilterOptions] = useState<string[]>([])
  const [subTypeFilterOptions, setSubTypeFilterOptions] = useState<string[]>([])
  const [colorTagFilterOptions, setColorTagFilterOptions] = useState<string[]>([])
  const [categoryFilterOptions, setCategoryFilterOptions] = useState<string[]>([])
  const [collectionFilterOptions, setCollectionFilterOptions] = useState<string[]>([])
  const [layoutFilterOptions, setLayoutFilterOptions] = useState<string[]>([])

  useEffect(() => {
    const cardData = extractCardData(cardsToFilter)

    setColorFilterOptions(cardData.colors)
    setKeywordFilterOptions(cardData.keywords)
    setTypeFilterOptions(cardData.types)
    setSuperTypeFilterOptions(cardData.superTypes)
    setSubTypeFilterOptions(cardData.subTypes)
    setColorTagFilterOptions(cardData.colorTags)
    setCategoryFilterOptions(cardData.categories)
    setCollectionFilterOptions(cardData.collectionStatus)
    setLayoutFilterOptions(cardData.layouts)
  }, [cardsToFilter])

  return [
    colorFilterOptions,
    keywordFilterOptions,
    typeFilterOptions,
    superTypeFilterOptions,
    subTypeFilterOptions,
    colorTagFilterOptions,
    categoryFilterOptions,
    collectionFilterOptions,
    layoutFilterOptions,
  ]
}

export default useGetAdvancedFilterOptions
